import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

type Props = {
  phrases: string[];
  className?: string;
  /** ms per character while writing */
  typeSpeed?: number;
  /** ms per character while erasing */
  eraseSpeed?: number;
  /** ms to rest on a completed phrase */
  hold?: number;
  /** ms before the first character appears */
  startDelay?: number;
};

/**
 * Deliberate, human-paced writing reveal. Height is reserved by an invisible
 * copy of the longest phrase, so nothing ever reflows while it types.
 */
export function Typewriter({
  phrases,
  className,
  typeSpeed = 58,
  eraseSpeed = 26,
  hold = 2400,
  startDelay = 700,
}: Props) {
  const reduced = useReducedMotion();
  const [text, setText] = useState(reduced ? phrases[0] : "");
  const [writing, setWriting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (reduced) {
      setText(phrases[0]);
      setWriting(false);
      return;
    }

    let i = 0;
    let chars = 0;
    let erasing = false;
    let cancelled = false;

    const step = (wait: number) => {
      timer.current = setTimeout(() => {
        if (cancelled) return;
        const phrase = phrases[i];

        if (!erasing) {
          chars += 1;
          setText(phrase.slice(0, chars));
          setWriting(true);
          if (chars >= phrase.length) {
            setWriting(false);
            if (phrases.length === 1) return; // settle, caret fades out
            erasing = true;
            step(hold);
          } else {
            // small natural rhythm variation, never gimmicky
            step(typeSpeed + (phrase[chars - 1] === " " ? 40 : Math.random() * 26));
          }
          return;
        }

        chars -= 1;
        setText(phrase.slice(0, chars));
        setWriting(true);
        if (chars <= 0) {
          erasing = false;
          i = (i + 1) % phrases.length;
          step(420);
        } else {
          step(eraseSpeed);
        }
      }, wait);
    };

    step(startDelay);
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [phrases, reduced, typeSpeed, eraseSpeed, hold, startDelay]);

  const longest = phrases.reduce((a, b) => (b.length > a.length ? b : a), "");

  return (
    <span className={`relative block ${className ?? ""}`}>
      {/* reserves the line box so the hero height never changes */}
      <span aria-hidden className="invisible block">
        {longest}
      </span>
      <span className="absolute inset-0 block" aria-live="polite">
        {text}
        <span
          aria-hidden
          className="ml-[0.08em] inline-block w-[0.055em] translate-y-[0.08em] self-stretch bg-current align-baseline transition-opacity duration-500"
          style={{
            height: "0.92em",
            opacity: reduced || !writing ? 0 : 1,
            animation: writing ? "xs-caret 1.05s steps(1,end) infinite" : undefined,
          }}
        />
      </span>
    </span>
  );
}
